import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { confirmResetPassword } from 'aws-amplify/auth';
import { Lock, Check, X, CheckCircle, ArrowLeft, KeyRound } from 'lucide-react';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'Reset Your Password';
  }, []);

  const passwordRules = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[^A-Za-z0-9]/.test(newPassword),
  };

  const allRulesPassed = Object.values(passwordRules).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && newPassword !== '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    if (!code.trim()) {
      setError('Please enter the verification code from your email.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!allRulesPassed) {
      setError('Password does not meet all requirements.');
      return;
    }

    setLoading(true);

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });

      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      // Map Cognito error codes to user-friendly messages
      switch (err.name) {
        case 'CodeMismatchException':
          setError('Invalid verification code. Please check and try again.');
          break;
        case 'ExpiredCodeException':
          setError('Verification code has expired. Please request a new one.');
          break;
        case 'InvalidPasswordException':
          setError('Password does not meet the requirements.');
          break;
        case 'LimitExceededException':
          setError('Too many attempts. Please try again later.');
          break;
        case 'UserNotFoundException':
          setError('No account found with this email address.');
          break;
        default:
          setError(err.message || 'Failed to reset password. Please try again.');
      }
    }

    setLoading(false);
  };

  const inputClasses = "w-full pl-11 pr-4 py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-shadow";

  const RuleItem = ({ passed, text }) => (
    <li className={`flex items-center gap-2 text-sm ${passed ? 'text-emerald-600' : 'text-slate-400'}`}>
      {passed ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
      {text}
    </li>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Recoverly</h1>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          {success ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Password updated!</h2>
              <p className="text-sm text-slate-500">Redirecting to sign in...</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold text-slate-900">Set new password</h2>
                <p className="mt-2 text-sm text-slate-500">
                  Enter the code from your email and create a new password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email (if not passed from previous page) */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className={inputClasses}
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                {/* Verification Code */}
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Verification Code
                  </label>
                  <div className="relative">
                    <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      id="code"
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                      className={inputClasses}
                      placeholder="Enter code from email"
                      autoComplete="one-time-code"
                    />
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-slate-700 mb-1.5">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className={inputClasses}
                      placeholder="Enter new password"
                    />
                  </div>
                </div>

                {newPassword && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs font-medium text-slate-600 mb-3">Password Requirements</p>
                    <ul className="space-y-1.5">
                      <RuleItem passed={passwordRules.length} text="At least 8 characters" />
                      <RuleItem passed={passwordRules.uppercase} text="One uppercase letter" />
                      <RuleItem passed={passwordRules.lowercase} text="One lowercase letter" />
                      <RuleItem passed={passwordRules.number} text="One number" />
                      <RuleItem passed={passwordRules.special} text="One special character" />
                    </ul>
                  </div>
                )}

                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className={inputClasses}
                      placeholder="Confirm new password"
                    />
                  </div>
                  {confirmPassword && !passwordsMatch && (
                    <p className="mt-1.5 text-sm text-amber-600">Passwords don't match</p>
                  )}
                  {passwordsMatch && (
                    <p className="mt-1.5 text-sm text-emerald-600 flex items-center gap-1">
                      <Check className="h-4 w-4" /> Passwords match
                    </p>
                  )}
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !allRulesPassed || !passwordsMatch || !code.trim()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update password'
                  )}
                </button>

                <div className="text-center pt-2">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to sign in
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
